import { BufferSizeType, RenderQuality } from '@readcloud/data';
import * as t from 'io-ts';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonGroup,
  Label,
  OverlayTrigger,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@readcloud/component-library';
import { getOrElse } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import packageJson from 'package.json';

const flow = t.union([t.literal('Horizontal'), t.literal('Vertical')]);
interface Props {
  renderCache: number;
  renderCacheOptions: number[];
  onRenderCacheChange: (cache: BufferSizeType) => void;
  renderCacheOptionsLabels: string[];
  renderQuality?: string;
  onRenderQualityChange: (quality: RenderQuality) => void;
  renderQualityOptions?: string[];
  onEnableWalkthrough: () => void;
  renderMode: 'canvas' | 'svg';
  renderModeOptions: Array<'canvas' | 'svg'>;
  onRenderModeChange: (p: 'canvas' | 'svg') => void;
  darkMode: boolean;
  onDarkModeChange: (darkMode: boolean) => void;
  flow: t.TypeOf<typeof flow>;
  onFlowChange: (val: t.TypeOf<typeof flow>) => void;
  continuous: boolean;
  onContinuousChange: (continuous: boolean) => void;
  gutterSize: number;
  onGutterSizeChange: (size: number) => void;
}

const Options = ({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  disabled?: boolean;
  label: string;
  options: { value: string | number; label?: string }[];
  value: string | number;
  onChange: (v: string) => void;
}) => (
  <div>
    <Label className="rc-flex rc-flex-col max-sm:rc-text-xs">{label}</Label>
    <ToggleButtonGroup
      type="single"
      ensureValue
      defaultValue={value.toString()}
      className={`rc-flex `}
      onValueChange={(value) => {
        onChange(value);
      }}
    >
      {options.map((option) => (
        <ToggleButton
          variant="outline"
          id={`${label}-${option.value.toString()}`}
          type="button"
          name={`${label}-${option.value.toString()}`}
          value={option.value.toString()}
          key={`${label}-${option.value.toString()}`}
          checked={value.toString() === option.value.toString()}
          className={`rc-w-full max-sm:rc-text-xs max-sm:rc-h-7`}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </div>
);

export const PDFSettings: React.FC<Props> = (props) => {
  const handleDarkModeChange = (val: string) => {
    if (val === 'true' || val === 'false') {
      props.onDarkModeChange(val === 'true' ? true : false);
    }
  };
  const handleFlowChange = (val: string) => {
    if ((typeof val === 'string' && val === 'true') || flow.is(val)) {
      props.onFlowChange(
        pipe(
          flow.decode(val),
          getOrElse(() => 'Horizontal' as t.TypeOf<typeof flow>)
        )
      );
    }
  };
  const handleGutterSizeChange = (val: string) => {
    const tempVal = parseInt(val);
    if (typeof tempVal === 'number') {
      props.onGutterSizeChange(tempVal);
    }
  };

  return (
    <div>
      <div className="rc-flex rc-flex-col rc-w-full rc-h-full rc-flex-shrink-0 rc-rounded-md rc-gap-2">
        {false && (
          <div className="rc-flex rc-w-full rc-justify-between rc-items-center">
            <OverlayTrigger
              overlay={
                <Tooltip id="tt-walkthrough">Enable walkthrough</Tooltip>
              }
              placement="top"
            >
              <Button
                size="sm"
                variant="light"
                rounded
                onClick={props.onEnableWalkthrough}
              >
                <FontAwesomeIcon icon={'info'} />
              </Button>
            </OverlayTrigger>
          </div>
        )}
        <span className='rc-text-xs md:rc-text-base'>Version: {packageJson.version}</span>
        <div className="rc-w-full">
          <hr />
        </div>
        <Options
          label="Dark Mode"
          value={props.darkMode.toString()}
          onChange={handleDarkModeChange}
          options={[
            { value: 'true', label: 'Enabled' },
            { value: 'false', label: 'Disabled' },
          ]}
        />
        <Options
          label="Page Flow"
          value={props.flow}
          onChange={handleFlowChange}
          options={[
            { value: 'Horizontal', label: 'Horizontal' },
            { value: 'Vertical', label: 'Vertical' },
          ]}
        />
        <Options
          disabled={!props.continuous}
          label="Gutter Size"
          value={props.gutterSize.toString()}
          onChange={handleGutterSizeChange}
          options={[
            { value: 0, label: 'None' },
            { value: 8, label: 'Small' },
            { value: 16, label: 'Medium' },
            { value: 32, label: 'Large' },
          ]}
        />
      </div>
    </div>
  );
};
